// --------------------- Phone numbers with country code -----------------------
// Stored as "+<dial code><national number>" with no spaces (e.g. "+919876543210"),
// which fits the DB column (varchar 15) and passes the backend isMobilePhone() check.
// Numbers saved before country codes existed have no "+" and are treated as India.

export const COUNTRY_CODES = [
  { iso: "IN", dial: "91", name: "India", min: 10, max: 10, pattern: /^[6-9]\d{9}$/ },
  { iso: "US", dial: "1", name: "United States / Canada", min: 10, max: 10, pattern: /^[2-9]\d{9}$/ },
  { iso: "GB", dial: "44", name: "United Kingdom", min: 10, max: 10 },
  { iso: "AE", dial: "971", name: "United Arab Emirates", min: 9, max: 9 },
  { iso: "SA", dial: "966", name: "Saudi Arabia", min: 9, max: 9 },
  { iso: "SG", dial: "65", name: "Singapore", min: 8, max: 8 },
  { iso: "AU", dial: "61", name: "Australia", min: 9, max: 9 },
  { iso: "DE", dial: "49", name: "Germany", min: 10, max: 11 },
  { iso: "FR", dial: "33", name: "France", min: 9, max: 9 },
  { iso: "CN", dial: "86", name: "China", min: 11, max: 11 },
  { iso: "JP", dial: "81", name: "Japan", min: 10, max: 10 },
  { iso: "NP", dial: "977", name: "Nepal", min: 10, max: 10 },
  { iso: "BD", dial: "880", name: "Bangladesh", min: 10, max: 10 },
  { iso: "LK", dial: "94", name: "Sri Lanka", min: 9, max: 9 },
  { iso: "PK", dial: "92", name: "Pakistan", min: 10, max: 10 },
];

export const DEFAULT_DIAL_CODE = "91";

// Longest dial codes first so prefix matching picks the right country
const BY_DIAL_LENGTH = [...COUNTRY_CODES].sort((a, b) => b.dial.length - a.dial.length);

export const getCountry = (dial) => COUNTRY_CODES.find((c) => c.dial === dial);

export const parsePhone = (value) => {
  const raw = String(value || "").trim();
  const digits = raw.replace(/\D/g, "");
  if (raw.startsWith("+")) {
    const country = BY_DIAL_LENGTH.find((c) => digits.startsWith(c.dial));
    if (country) return { dial: country.dial, number: digits.slice(country.dial.length) };
  }
  return { dial: DEFAULT_DIAL_CODE, number: digits };
};

export const buildPhone = (dial, number) => (number ? `+${dial}${number}` : "");

export const sanitizePhoneNumber = (dial, value) =>
  String(value || "").replace(/\D/g, "").slice(0, getCountry(dial)?.max || 15);

// Returns "" when valid (or empty), otherwise an error message
export const phoneError = (value) => {
  if (!value) return "";
  const { dial, number } = parsePhone(value);
  const country = getCountry(dial);
  if (!country) return "Select a valid country code";
  const lengthOk = number.length >= country.min && number.length <= country.max;
  if (lengthOk && (!country.pattern || country.pattern.test(number))) return "";
  const digits = country.min === country.max ? country.min : `${country.min}-${country.max}`;
  return `Enter a valid ${digits}-digit number for ${country.name}`;
};

export const isValidPhone = (value) => !!value && phoneError(value) === "";

// "+919876543210" -> "+91 9876543210"; legacy numbers get the default code
export const formatPhone = (value) => {
  if (!value) return "";
  const { dial, number } = parsePhone(value);
  return number ? `+${dial} ${number}` : "";
};
